/**
 * `AssignCaseDialog` — assign (or re-parent) an UNASSIGNED case to a client and,
 * optionally, a matter. Reached from the navigator's "Unassigned" bucket per-row
 * affordance.
 *
 * Backend: the `case.assign` gateway RPC (D-5 deferred-assignment) — a metadata-only
 * op relayed through lexserver like `case.update`. On success the runtime broadcasts
 * `case.updated{kind:"reassigned"}`, so every connected client live-refreshes; we
 * ALSO invalidate our own caches deterministically for instant feedback on close.
 * FLAT cases only — a legacy (unmigrated) case is refused server-side.
 *
 * **id-based (no resolve-or-create):** `case.assign` assigns by id, so only an
 * EXISTING client/matter pick carries a usable id. A typed brand-NEW name has no id,
 * so the submit is gated to existing picks and a hint explains that a new
 * client/matter must be created via the New-Case chain first. The picked LABELS ride
 * along as `clientName`/`matterName` to refresh the D-12 display snapshot (labels
 * only — never resolve-or-create).
 *
 * Coherence (runtime-enforced): assigning to a matter requires BOTH clientId +
 * matterId together. A matter is only pickable under an EXISTING client (and the
 * matter selection resets when the client changes), so we always send the pair.
 *
 * Modal by design (mirrors `NewCaseDialog`): a click outside does NOT close it —
 * only Cancel / X / Esc. The op-verb button + spinner is the in-flight cue.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogHeaderCls,
  dialogTitleCls,
  dialogCloseBtnCls,
  dialogLabelCls,
  dialogInputCls,
  dialogFooterCls,
  dialogCancelBtnCls,
  dialogPrimaryBtnCls,
} from "@/components/context-pane/dialog-styles";
import { useToastStore } from "@/stores/useToastStore";
import { useClients } from "@/hooks/useClients";
import { useMatters } from "@/hooks/useMatters";
import { normalizeMatterType } from "@/lib/case-labels";
import { resolveDisplayName } from "@/lib/entity-display";
import { useLanguageStore } from "@/stores/useLanguageStore";
import {
  NameOrPickField,
  hasNewName,
  type NameOrPickValue,
} from "@/components/ui/NameOrPickField";
import { type CaseListItem } from "@/lib/lexserver-client";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { CaseAssignParams, CaseAssignResult } from "@/types/gateway";

/**
 * Pure params-builder — maps the dialog's client/matter picks to `case.assign`
 * params (sans `caseId`). Exported for unit testing.
 *
 * The dialog requires a CLIENT (matter is optional, picked under it). `case.assign`
 * is id-based, so only an EXISTING pick yields a usable id; a typed NEW name has no
 * id and is ignored here (the dialog gates submit + hints instead). The picked
 * LABELS ride along as `clientName`/`matterName` for the D-12 display snapshot.
 * Returns `null` when no existing client is picked (≥1 id is required + a matter
 * needs its client), so the caller can disable submit. A matter is only ever
 * included WITH its client (coherence — a matter belongs to one client).
 */
export function buildCaseAssignParams(input: {
  clientValue: NameOrPickValue;
  matterValue: NameOrPickValue;
}): Omit<CaseAssignParams, "caseId"> | null {
  const client = input.clientValue?.mode === "existing" ? input.clientValue : undefined;
  if (!client) return null;
  const params: Omit<CaseAssignParams, "caseId"> = {
    clientId: client.id,
    clientName: client.label,
  };
  const matter = input.matterValue?.mode === "existing" ? input.matterValue : undefined;
  if (matter) {
    params.matterId = matter.id;
    params.matterName = matter.label;
  }
  return params;
}

interface AssignCaseDialogProps {
  caseItem: CaseListItem;
  onClose: () => void;
  /** Fired after a successful assign so the parent can react (the caches are
   *  already invalidated here). */
  onAssigned?: (result: CaseAssignResult) => void;
}

export function AssignCaseDialog({ caseItem, onClose, onAssigned }: AssignCaseDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const rpc = useGatewayStore((s) => s.rpc);
  const addToast = useToastStore((s) => s.addToast);
  const lang = useLanguageStore((s) => s.lang);

  const { data: clients } = useClients();

  const [clientValue, setClientValue] = useState<NameOrPickValue>(null);
  const [matterValue, setMatterValue] = useState<NameOrPickValue>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Existing matters are only pickable under an EXISTING client; the query
  // auto-disables when the clientId is undefined.
  const selectedClientId = clientValue?.mode === "existing" ? clientValue.id : undefined;
  const { data: matters } = useMatters(selectedClientId);

  const clientOptions = useMemo(
    () =>
      (clients ?? []).map((c) => ({
        id: c.clientId,
        label: c.name,
        sublabel: c.alias ? `@${c.alias}` : undefined,
      })),
    [clients],
  );
  const matterOptions = useMemo(
    () =>
      (matters ?? []).map((m) => ({
        id: m.matterId,
        label: resolveDisplayName(m, lang),
        sublabel: t(`newCase.matterTypes.${normalizeMatterType(m.matterType)}`, {
          defaultValue: normalizeMatterType(m.matterType),
        }),
      })),
    [matters, t, lang],
  );
  // L7: no "Assunto" NEW-name suggestions in the ASSIGN dialog — case.assign is
  // id-based (no resolve-or-create), so a suggested new matter name would commit
  // as mode:"new" and dead-end the submit (blocked by the hasNewSelection gate).
  // Only EXISTING matters are assignable; the New-Case dialog keeps the suggestions.

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const assignParams = buildCaseAssignParams({ clientValue, matterValue });
  // A typed NEW client/matter name has no id → the id-based case.assign can't take
  // it. Gate submit (rather than silently dropping the typed name) and show a hint.
  //
  // ⚖ **D-98 / C-2 — BOTH non-existing modes are inert here, and that identity is
  // the invariant.** This dialog ASSIGNS existing entities; it has no
  // resolve-or-create path and sends no `createParents`. So `typed` and `new`
  // must behave IDENTICALLY — the test asserts they produce the same outcome
  // rather than asserting each is ignored, precisely so nobody later reads
  // `mode:"new"` as "the user committed, so we may act on it" and wires a create
  // into an assign. The commit distinction is meaningful ONLY where a create is
  // actually possible, which is `NewCaseDialog`.
  const hasNewSelection = hasNewName(clientValue) || hasNewName(matterValue);
  // Include `!!rpc` (L6): without a live gateway the button would appear enabled but
  // handleAssign bare-returns on `!rpc` → a silent no-op (reachable on a mid-dialog
  // per-user-container roll). Disabling the button is the honest cue.
  const canAssign = !!rpc && !!assignParams && !hasNewSelection && !submitting;

  const caseTitle = resolveDisplayName(
    { name: caseItem.title, slug: caseItem.slug, id: caseItem.caseId },
    lang,
  );

  const handleAssign = useCallback(async () => {
    if (!rpc || !assignParams || hasNewSelection || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await rpc.caseAssign({ caseId: caseItem.caseId, ...assignParams });
      // case.assign broadcasts `case.updated{kind:"reassigned"}` (every connected
      // client refreshes via OverviewNav's stream) — but we ALSO invalidate locally
      // for instant feedback on close. Refresh the cross-case navigator list
      // (`overview-cases`, whose Unassigned bucket the assigned case must drop off)
      // + the flat `["cases"]` list, plus the destination client's matter cache
      // (the case joined a matter group).
      void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
      if (result.clientId) {
        void queryClient.invalidateQueries({ queryKey: ["matters", result.clientId] });
      }
      addToast({
        text: t("assignCase.toastAssigned", { title: caseTitle }),
        kind: "success",
        autoDismissMs: 4000,
      });
      onAssigned?.(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("assignCase.errors.generic"));
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpc, assignParams, hasNewSelection, submitting, caseItem.caseId, queryClient, addToast, caseTitle, t, onAssigned, onClose]);

  const inputCls = dialogInputCls;
  const labelCls = dialogLabelCls;

  return (
    <div data-testid="assign-case-dialog" className={dialogOverlayCls}>
      <div role="dialog" aria-modal="true" aria-label={t("assignCase.title")} className={dialogPanelCls}>
        <div className={dialogHeaderCls}>
          <h2 className={dialogTitleCls}>{t("assignCase.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("actions.close")}
            className={dialogCloseBtnCls}
          >
            <X size={16} />
          </button>
        </div>
        <p className="mb-4 truncate text-body-sm text-coffee-600 dark:text-cream-400" title={caseTitle}>
          {caseTitle}
        </p>

        {/* CLIENT — pick an existing client (required) */}
        <label className={labelCls}>{t("assignCase.clientLabel")}</label>
        <NameOrPickField
          data-testid="assign-case-client"
          options={clientOptions}
          value={clientValue}
          onChange={(v) => {
            setClientValue(v);
            // A different client invalidates the matter selection.
            setMatterValue(null);
          }}
          placeholder={t("assignCase.clientPlaceholder")}
          createNoun={t("newCase.createNounClient")}
          createLabel={(name) => t("newCase.createEntity", { name })}
          reuseLabel={(name) => t("newCase.reuseEntity", { name })}
          inputClassName={inputCls}
        />

        {/* MATTER — optional; pick an existing matter under the chosen client */}
        <div className="mb-1 mt-4 flex items-center justify-between">
          <span className={cn(labelCls, "mb-0")}>{t("assignCase.matterLabel")}</span>
          <span className="text-caption text-cream-500">{t("assignCase.matterOptional")}</span>
        </div>
        <NameOrPickField
          data-testid="assign-case-matter"
          options={clientValue?.mode === "existing" ? matterOptions : []}
          value={matterValue}
          onChange={setMatterValue}
          disabled={!clientValue}
          placeholder={
            clientValue ? t("assignCase.matterPlaceholder") : t("assignCase.matterPlaceholderNoClient")
          }
          createNoun={t("newCase.createNounMatter")}
          createLabel={(name) => t("newCase.createEntity", { name })}
          reuseLabel={(name) => t("newCase.reuseEntity", { name })}
          inputClassName={inputCls}
          maxLength={24}
        />

        {hasNewSelection && (
          <p
            data-testid="assign-case-new-hint"
            className="mt-2 rounded-lg bg-amber-50 p-2.5 text-caption text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          >
            {t("assignCase.newEntityHint")}
          </p>
        )}

        {error && (
          <p
            data-testid="assign-case-error"
            className="mt-3 rounded-lg bg-red-50 p-2.5 text-body-sm text-red-700 dark:bg-red-950 dark:text-red-400"
          >
            {error}
          </p>
        )}

        <div className={dialogFooterCls}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className={dialogCancelBtnCls}
          >
            {t("actions.cancel")}
          </button>
          <button
            type="button"
            data-testid="assign-case-submit"
            onClick={() => void handleAssign()}
            disabled={!canAssign}
            className={dialogPrimaryBtnCls(canAssign)}
          >
            {submitting && <Loader2 size={13} className="animate-spin" />}
            {t("assignCase.assign")}
          </button>
        </div>
      </div>
    </div>
  );
}
